import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isDefined } from '../../../../utils/variables';
import { useProgressBar } from '../../../hooks/use-progress-bar';
import { Button } from '../../button/button';
import { CheckBox } from '../../check-box/check-box';
import { Dialog } from '../../dialog/dialog';
import { InfoBox } from '../../info-box/info-box';
import { Stack } from '../../stack/stack';
import { GeodataImageItemWithMarkers } from '../gcp-page/place-markers-state';
import { usePatchGeodataImagesMutation } from './use-patch-geodata-images-mutation';

type Props = {
  items: GeodataImageItemWithMarkers[];
  geodataId: string;
  onClose: () => void;
  photoCount: number;
};

export const BulkUpdateImagesDialog = ({ items, geodataId, photoCount, onClose }: Props) => {
  const { t } = useTranslation();
  const { patchGeodataImagesAsync, isPending, errorList } = usePatchGeodataImagesMutation(
    geodataId,
    photoCount,
  );
  const { onProgress, renderProgressBar } = useProgressBar({ hideWhenIdle: true });

  // Calculate default state: true if SOME items are true, false if ALL are false
  const defaultEnabled = useMemo(() => {
    return items.some((item) => item.enabled);
  }, [items]);

  const defaultUseGps = useMemo(() => {
    return items.some((item) => item.useGps);
  }, [items]);

  const [enabled, setEnabled] = useState<boolean>(defaultEnabled);
  const [useGpsPosition, setUseGpsPosition] = useState<boolean>(defaultUseGps);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    const imageIds = items.filter((item) => isDefined(item.imageId)).map((item) => item.imageId!);
    const chunkSize = 1000;
    const chunks: string[][] = [];

    // Split into chunks of 1000
    for (let i = 0; i < imageIds.length; i += chunkSize) {
      chunks.push(imageIds.slice(i, i + chunkSize));
    }

    setIsSubmitting(true);
    onProgress({
      current: 0,
      total: chunks.length,
    });

    try {
      // Process chunks sequentially
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        await patchGeodataImagesAsync({
          path: { geodataId },
          body: {
            ids: chunk,
            enabled,
            useGpsPosition,
          },
        });

        // Update progress after each chunk
        onProgress({
          current: i + 1,
          total: chunks.length,
        });
      }

      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog closeIcon={true} onClose={onClose}>
      {{
        header: t('gcpPage.bulkUpdateImagesDialog.title', { ns: 'cloudProcessing' }),
        content: (
          <Stack spacing={1}>
            <InfoBox color="yellow">
              {t('gcpPage.bulkUpdateImagesDialog.info', {
                ns: 'cloudProcessing',
                count: items.length,
              })}
            </InfoBox>
            <CheckBox
              checked={enabled}
              disabled={isPending || isSubmitting}
              onChange={(e) => setEnabled(e.target.checked)}
            >
              {t('gcpPage.camerasDialog.table.usePhoto', { ns: 'cloudProcessing' })}
            </CheckBox>
            <CheckBox
              checked={useGpsPosition}
              disabled={isPending || isSubmitting}
              onChange={(e) => setUseGpsPosition(e.target.checked)}
            >
              {t('gcpPage.camerasDialog.table.useGpsPositioning', { ns: 'cloudProcessing' })}
            </CheckBox>
            {renderProgressBar()}
            {errorList}
          </Stack>
        ),
        footer: {
          right: (
            <>
              <Button
                color="primary"
                disabled={isPending || isSubmitting}
                loading={isPending || isSubmitting}
                variant="contained"
                onClick={handleSubmit}
              >
                {t('update', { ns: 'common' })}
              </Button>
              <Button disabled={isPending || isSubmitting} variant="text" onClick={onClose}>
                {t('cancel', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};
